import axios from 'axios';
import * as React from 'react';
import { useRecoilValue } from 'recoil';
import { AFormFields, Field } from '../../atoms/forms';
import { URL_FOUNDATION } from '../../config/keys';
import { AlertCall, AlertRes } from '../alert/AlertProvider';
import Dropdown from '../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../components_v2/dropdown/model/Model';
import { DefaultButton } from '../../styles/global/css/GlobalButton';
import { GreySidely3 } from '../../styles/global/css/Utils';
import { Translate, translateToString } from '../../styles/global/translate';
import { creationPopupDropdownStyle } from '../../components_v2/popup/style/CreationFormStyle';
import { launchToastError } from '../forms/visual-form-editor/visualFormEditor';
import { importWrapper } from '../../utils/import';
import { bestMatch, normalizeHeader } from '../promotions/import';
import { FlexDiv } from './style';
import { getBrands, getCategories, getStatuses } from './action';

// The popup import of the products page, in the spirit of the promotion
// import: a mapping popup (auto-filled from the export's own headers, saved
// per browser), a pivot column to update instead of duplicating (EAN by
// default), the product-level field values included — one transaction on the
// server, no redis, unlike the classic import module.

type ProductPivot = 'barcode' | 'reference' | 'name';
export type ImportedProduct = {
	id?: number,
	name?: string,
	reference?: string,
	barcode?: string,
	code_upc?: string,
	category_id?: number,
	brand_id?: number,
	status_id?: number,
	stock?: number,
	price?: number,
	recommended_price?: number,
	outer?: number,
	summary?: string,
	description?: string,
	width?: number,
	height?: number,
	depth?: number,
	fields?: Record<number, unknown>,
};
type ImportReport = { created: number, updated: number, skipped: number[] };

function importProductsCall(products: ImportedProduct[], pivot: ProductPivot | undefined): Promise<ImportReport> {
	return axios.post(`${URL_FOUNDATION}/api/v2/products/import`, { products, pivot }).then(res => res.data);
}

type BaseTargetKey = 'id' | 'name' | 'reference' | 'barcode' | 'code_upc' | 'brand' | 'category' | 'availability' | 'stock'
	| 'price' | 'recommended_price' | 'outer' | 'summary' | 'description' | 'width' | 'height' | 'depth';
type TargetKey = BaseTargetKey | `field_${number}`;
type TargetKind = 'text' | 'number' | 'integer';

// candidates match the export's english headers first, then the display names
const BASE_TARGETS: { key: BaseTargetKey, label: () => string, candidates: string[], kind: TargetKind, required?: boolean }[] = [
	{ key: 'id', label: () => 'Id', candidates: ['id'], kind: 'integer' },
	{ key: 'name', label: () => translateToString('name'), candidates: ['name'], kind: 'text', required: true },
	{ key: 'reference', label: () => translateToString('reference'), candidates: ['reference'], kind: 'text' },
	{ key: 'barcode', label: () => translateToString('barcode'), candidates: ['barcode', 'code barre', 'ean', 'gencod'], kind: 'text' },
	{ key: 'code_upc', label: () => 'Code UPC', candidates: ['code_upc', 'upc'], kind: 'text' },
	{ key: 'brand', label: () => translateToString('brand'), candidates: ['brand'], kind: 'text' },
	{ key: 'category', label: () => translateToString('category'), candidates: ['category'], kind: 'text' },
	{ key: 'availability', label: () => translateToString('Avaibility'), candidates: ['availability', 'disponibilite', 'statut'], kind: 'text' },
	{ key: 'stock', label: () => 'Stock', candidates: ['stock'], kind: 'integer' },
	{ key: 'price', label: () => translateToString('price'), candidates: ['price', 'prix'], kind: 'number' },
	{ key: 'recommended_price', label: () => translateToString('recommended price'), candidates: ['recommended_price', 'prix conseille'], kind: 'number' },
	{ key: 'outer', label: () => 'Outer', candidates: ['outer'], kind: 'integer' },
	{ key: 'summary', label: () => translateToString('summary'), candidates: ['summary', 'resume'], kind: 'text' },
	{ key: 'description', label: () => translateToString('description'), candidates: ['description'], kind: 'text' },
	{ key: 'width', label: () => translateToString('width'), candidates: ['width', 'largeur'], kind: 'number' },
	{ key: 'height', label: () => translateToString('height'), candidates: ['height', 'hauteur'], kind: 'number' },
	{ key: 'depth', label: () => translateToString('depth'), candidates: ['depth', 'profondeur'], kind: 'number' },
];

const MAPPING_STORAGE_KEY = 'products_import_mapping';
type SavedMapping = { columns?: Partial<Record<string, string>>, pivot?: ProductPivot };
function loadSavedMapping(): SavedMapping {
	try { return JSON.parse(localStorage.getItem(MAPPING_STORAGE_KEY) ?? '{}'); } catch { return {}; }
}

type MappingResult = { mapping: Map<TargetKey, number>, pivot: ProductPivot | undefined };

// a warning is a cell the import kept the row for but could not apply: an
// unknown brand/category/status name, or an unparsable typed value
export type ImportWarning = { line: number, column: string, value: string };

function warningsCsv(warnings: ImportWarning[]): void {
	const quote = (value: string) => `"${value.replace(/"/g, '""')}"`;
	const rows = [
		[translateToString('import.promotions.skipped.line'), translateToString('import.products.column'), translateToString('import.products.value')].map(quote).join(';'),
		...warnings.map(w => [String(w.line), quote(w.column), quote(w.value)].join(';')),
	];
	const blob = new Blob(['﻿' + rows.join('\n')], { type: 'text/csv;charset=utf-8' });
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = 'import_warnings.csv';
	anchor.click();
	URL.revokeObjectURL(url);
}

function parseFieldValue(field: Field, raw: unknown): unknown {
	const text = String(raw).trim();
	if (text === '') return undefined;
	switch (field.type) {
		case 'Integer': {
			const parsed = parseInt(text);
			return isNaN(parsed) ? undefined : parsed;
		}
		case 'Number': {
			const parsed = parseFloat(text.replaceAll(',', '.'));
			return isNaN(parsed) ? undefined : parsed;
		}
		case 'Money': {
			const parsed = parseFloat(text.replaceAll(',', '.'));
			return isNaN(parsed) ? undefined : Math.round(parsed * 100);
		}
		case 'Boolean': {
			const lower = text.toLowerCase();
			if (['true', 'oui', 'yes', '1', 'vrai'].includes(lower)) return true;
			if (['false', 'non', 'no', '0', 'faux'].includes(lower)) return false;
			return undefined;
		}
		case 'Multiselect':
			return text.split(',').map(v => v.trim()).filter(v => v.length > 0);
		default:
			return text;
	}
}

export default function importProducts(
	onImport: (importCall: (() => Promise<void>) | undefined) => void,
	alert: AlertCall,
	fields: Field[],
	onImported: (report: ImportReport) => void,
) {
	const productFields = fields.filter(f => f.is_product);
	importWrapper<never>(async(data) => {
		if (!Array.isArray(data[0])) {
			launchToastError(translateToString('import.promotions.errors.bad_file'));
			return onImport(undefined);
		}
		const rawHeaders: unknown[] = data[0];

		const alertRes = await alert<MappingResult>({
			title: <Translate id='general.import' />,
			buttonMargin: '0px',
			noButtons: true,
			overflow: 'unset',
			width: 'min(760px, 95vw)',
			content: (_, onOk) => {
				const allFields = useRecoilValue(AFormFields);
				const popupFields = React.useMemo(() => allFields.filter(f => f.is_product), [allFields]);
				const normHeaders = React.useMemo(() => rawHeaders.map(normalizeHeader), []);
				const columnOptions = React.useMemo<DropdownData<number>[]>(
					() => rawHeaders.map((h, i) => ({ label: String(h ?? '').trim() || `${translateToString('import.promotions.file_column')} ${i + 1}`, value: i })),
					[],
				);

				// the saved mapping restores first (exact header text), then the
				// auto-fill completes the rest; each column is claimed only once
				const init = React.useMemo(() => {
					const saved = loadSavedMapping();
					const used = new Set<number>();
					const resolve = (header?: string): number | undefined => {
						if (!header) return undefined;
						const i = normHeaders.findIndex((h, idx) => !used.has(idx) && h === header);
						if (i >= 0) used.add(i);
						return i >= 0 ? i : undefined;
					};
					const take = (candidates: string[]): number | undefined => {
						const i = bestMatch(normHeaders, candidates, used);
						if (i !== undefined) used.add(i);
						return i;
					};
					const mapping = new Map<TargetKey, number>();
					for (const target of BASE_TARGETS) {
						const i = resolve(saved.columns?.[target.key]) ?? take([target.label(), ...target.candidates]);
						if (i !== undefined) mapping.set(target.key, i);
					}
					for (const field of popupFields) {
						const i = resolve(saved.columns?.[`field_${field.id}`]) ?? take([field.name]);
						if (i !== undefined) mapping.set(`field_${field.id}`, i);
					}
					// EAN is the usual update key: prefer it whenever mapped
					const pivot: ProductPivot | undefined = saved.pivot && mapping.has(saved.pivot) ? saved.pivot
						: mapping.has('barcode') ? 'barcode'
							: mapping.has('reference') ? 'reference'
								: undefined;
					return { mapping, pivot };
				}, [popupFields]);

				const [mapping, setMapping] = React.useState<Map<TargetKey, number>>(init.mapping);
				const [pivot, setPivot] = React.useState<ProductPivot | undefined>(init.pivot);
				// "ID externe": replaces the file id with the pivot column,
				// exactly like the promotion import's checkbox under Id
				const [externalId, setExternalId] = React.useState(init.pivot !== undefined);

				const setTarget = (key: TargetKey, index?: number) => setMapping(prev => {
					const next = new Map(prev);
					if (index === undefined) next.delete(key);
					else next.set(key, index);
					return next;
				});

				const pivotOptions = React.useMemo<DropdownData<ProductPivot | undefined>[]>(() =>
					(['barcode', 'reference', 'name'] as const).filter(p => mapping.has(p))
						.map(p => ({ label: BASE_TARGETS.find(t => t.key === p)!.label(), value: p as ProductPivot | undefined })),
				[mapping]);

				const submit = () => {
					if (externalId && pivot === undefined) {
						launchToastError(translateToString('import.promotions.external_id_pick'));
						return;
					}
					if (!externalId && !mapping.has('name') && !mapping.has('id')) {
						launchToastError(translateToString('import.products.name_required'));
						return;
					}
					// the external id replaces the file id entirely
					const effectiveMapping = new Map(mapping);
					if (externalId) effectiveMapping.delete('id');
					try {
						const saved: SavedMapping = {
							columns: Object.fromEntries([...mapping.entries()].map(([key, i]) => [key, normHeaders[i]])),
							pivot: externalId ? pivot : undefined,
						};
						localStorage.setItem(MAPPING_STORAGE_KEY, JSON.stringify(saved));
					} catch { /* empty */ }
					onOk({ mapping: effectiveMapping, pivot: externalId ? pivot : undefined });
				};

				const tableGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: '190px minmax(0, 1fr)', alignItems: 'center', rowGap: '10px', columnGap: '12px', width: '100%' };
				const spanRow: React.CSSProperties = { gridColumn: '1 / -1' };
				const row = (key: TargetKey, label: string, required?: boolean, disabled?: boolean) => <React.Fragment key={key}>
					<h5 title={label} style={{ margin: 0, fontSize: '13px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
						{label}{required && !externalId ? ' *' : ''}
					</h5>
					<Dropdown<number>
						cancellable
						disabled={disabled}
						dropdownStyle={creationPopupDropdownStyle}
						name=''
						datalist={columnOptions}
						selectedValue={mapping.has(key) ? columnOptions.find(o => o.value === mapping.get(key)) : undefined}
						onChange={v => setTarget(key, v.value ?? undefined)}
					/>
				</React.Fragment>;

				return <FlexDiv flow='column' align='stretch' gap='12px' style={{ width: '100%', textAlign: 'initial', maxHeight: '70vh', overflowY: 'auto', paddingRight: '8px' }}>
					<p style={{ margin: 0, fontSize: '12px', color: GreySidely3 }}><Translate id='import.products.hint' /></p>
					<div style={tableGrid}>
						{row('id', 'Id', false, externalId)}
						<div style={spanRow}>
							<label style={{ display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer', margin: 0, fontSize: '12px' }}>
								<input type='checkbox' checked={externalId} onChange={() => setExternalId(v => !v)} />
								<Translate id='import.promotions.external_id' />
							</label>
						</div>
						{externalId && <React.Fragment>
							<h5 style={{ margin: 0, fontSize: '13px', fontWeight: 500 }}><Translate id='import.products.pivot' /></h5>
							<Dropdown<ProductPivot | undefined>
								dropdownStyle={creationPopupDropdownStyle}
								name=''
								datalist={pivotOptions}
								selectedValue={pivotOptions.find(o => o.value === pivot)}
								onChange={v => setPivot(v.value ?? undefined)}
							/>
							<p style={{ ...spanRow, margin: 0, fontSize: '12px', color: GreySidely3 }}><Translate id='import.products.pivot_hint' /></p>
						</React.Fragment>}
						{BASE_TARGETS.filter(target => target.key !== 'id').map(target => row(target.key, target.label(), target.required))}
						{popupFields.map(field => row(`field_${field.id}`, field.name))}
					</div>
					<FlexDiv justify='center' style={{ width: '100%' }}>
						<DefaultButton margin='15px 0 0 0' onClick={submit}><Translate id='general.import' /></DefaultButton>
					</FlexDiv>
				</FlexDiv>;
			},
		});
		if (alertRes.res == AlertRes.Break) return onImport(undefined);
		const { mapping, pivot } = alertRes.data;

		// name -> id resolutions are client-side, like the promotion import
		// resolves barcodes: unknown names keep the row and land in warnings
		const [brands, categories, statuses] = await Promise.all([
			getBrands().then(res => res.data).catch(() => []),
			getCategories().then(res => {
				type RawCategory = { id: number, name: string, children?: RawCategory[] };
				const flatten = (list: RawCategory[]): { id: number, name: string }[] => list.flatMap(c => [{ id: c.id, name: c.name }, ...flatten(c.children ?? [])]);
				return flatten(res.data);
			}).catch(() => []),
			getStatuses().then(res => res.data).catch(() => []),
		]);
		const byName = (list: { id: number, name: string }[]) => new Map(list.map(e => [e.name.trim().toLowerCase(), e.id]));
		const brandMap = byName(brands);
		const categoryMap = byName(categories);
		const statusMap = byName(statuses);

		const warnings: ImportWarning[] = [];
		const columnName = (index: number) => String(rawHeaders[index] ?? '').trim() || `${translateToString('import.promotions.file_column')} ${index + 1}`;
		const cell = (rowValues: unknown[], key: TargetKey): unknown => {
			const index = mapping.get(key);
			if (index === undefined) return undefined;
			const value = rowValues[index];
			return value === '' || value === null ? undefined : value;
		};
		const text = (rowValues: unknown[], key: TargetKey): string | undefined => {
			const value = cell(rowValues, key);
			return value === undefined ? undefined : String(value).trim() || undefined;
		};
		const numeric = (rowValues: unknown[], key: TargetKey, line: number, integer: boolean): number | undefined => {
			const value = cell(rowValues, key);
			if (value === undefined) return undefined;
			const parsed = integer ? parseInt(String(value)) : parseFloat(String(value).replaceAll(',', '.'));
			if (isNaN(parsed)) {
				warnings.push({ line, column: columnName(mapping.get(key)!), value: String(value) });
				return undefined;
			}
			return parsed;
		};
		const resolved = (map: Map<string, number>, rowValues: unknown[], key: TargetKey, line: number): number | undefined => {
			const value = text(rowValues, key);
			if (value === undefined) return undefined;
			const id = map.get(value.toLowerCase());
			if (id === undefined) warnings.push({ line, column: columnName(mapping.get(key)!), value });
			return id;
		};

		const products: ImportedProduct[] = [];
		data.forEach((rowValues, rowIndex) => {
			if (rowIndex === 0 || !Array.isArray(rowValues)) return;
			if (rowValues.length === 0 || rowValues.every(value => value === undefined || value === null || value === '')) return;
			const line = rowIndex + 1;
			const fieldValues: Record<number, unknown> = {};
			for (const field of productFields) {
				const raw = cell(rowValues, `field_${field.id}`);
				if (raw === undefined) continue;
				const parsed = parseFieldValue(field, raw);
				if (parsed === undefined) {
					warnings.push({ line, column: columnName(mapping.get(`field_${field.id}`)!), value: String(raw) });
					continue;
				}
				fieldValues[field.id] = parsed;
			}
			products.push({
				id: numeric(rowValues, 'id', line, true),
				name: text(rowValues, 'name'),
				reference: text(rowValues, 'reference'),
				barcode: text(rowValues, 'barcode'),
				code_upc: text(rowValues, 'code_upc'),
				brand_id: resolved(brandMap, rowValues, 'brand', line),
				category_id: resolved(categoryMap, rowValues, 'category', line),
				status_id: resolved(statusMap, rowValues, 'availability', line),
				stock: numeric(rowValues, 'stock', line, true),
				price: numeric(rowValues, 'price', line, false),
				recommended_price: numeric(rowValues, 'recommended_price', line, false),
				outer: numeric(rowValues, 'outer', line, true),
				summary: text(rowValues, 'summary'),
				description: text(rowValues, 'description'),
				width: numeric(rowValues, 'width', line, false),
				height: numeric(rowValues, 'height', line, false),
				depth: numeric(rowValues, 'depth', line, false),
				fields: Object.keys(fieldValues).length > 0 ? fieldValues : undefined,
			});
		});
		if (products.length === 0) {
			launchToastError(translateToString('import.promotions.errors.bad_file'));
			return onImport(undefined);
		}

		onImport(() => importProductsCall(products, pivot).then(report => {
			onImported(report);
			alert({
				title: translateToString('import.products.summary_title'),
				width: 'min(520px, 95vw)',
				content: () => <FlexDiv flow='column' align='stretch' gap='10px'>
					<p style={{ margin: 0, fontSize: '13px' }}>
						{translateToString('import.products.summary', [
							['created', String(report.created)],
							['updated', String(report.updated)],
							['skipped', String(report.skipped.length)],
						])}
					</p>
					{report.skipped.length > 0 && <p style={{ margin: 0, fontSize: '12px', color: GreySidely3 }}>
						{translateToString('import.products.skipped_lines', [['lines', report.skipped.slice(0, 30).join(', ')]])}
					</p>}
					{warnings.length > 0 && <>
						<p style={{ margin: 0, fontSize: '12px', color: GreySidely3 }}>
							{translateToString('import.products.warnings', [['count', String(warnings.length)]])}
						</p>
						<FlexDiv justify='center'>
							<DefaultButton margin='5px 0 0 0' onClick={() => warningsCsv(warnings)}><Translate id='import.promotions.skipped.download' /></DefaultButton>
						</FlexDiv>
					</>}
				</FlexDiv>,
			});
		}));
	}, alert);
}
